import React, { createContext, useContext, useMemo } from 'react';
import { serverTimestamp } from 'firebase/firestore';
import { writeBatch } from './writes';
import { docRef, COLLECTIONS } from './paths';
import { db } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import { coreSync } from '../lib/core';
import { freeId } from '../domain/freeId';
import { owedByFamilies, transactionDocument, voidEntryDocument } from '../domain/familyAccount';
import { useCollectionWindow } from './useCollectionWindow';

// The family ledger.
//
// This provider deliberately exposes no way to write a balance. Callers append
// entries; balances are folded from them on read. Every screen that shows money
// — the family list header, the family ledger table, the students list — reads
// from the same fold, so they cannot disagree with each other.

const LedgerContext = createContext(null);

// Bounded for the same reason SyncContext bounds its listeners: an unbounded
// listener on a money collection has no memory ceiling.
const TRACK_LIMIT = 5000;

export function LedgerProvider({ children }) {
  const { user } = useAuth();

  // The money ledger window, from whichever read path serves this session
  // (#207 U5).
  //
  // `withMetadata` keeps the snapshot path's includeMetadataChanges, so the UI
  // can distinguish a locally-appended entry that has not reached the server
  // yet — that is what drives the pending dot beside a row the teacher just
  // added while offline. On the store path `pendingWrite` comes from our own
  // queue instead, and the flag is dropped there by the hook.
  const read = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.ledgerEntries,
    limit: TRACK_LIMIT,
    withMetadata: true,
  });
  const entries = read.docs;
  const loading = read.loading;

  const byFamily = useMemo(() => {
    const map = new Map();
    for (const e of entries) {
      if (!map.has(e.familyId)) map.set(e.familyId, []);
      map.get(e.familyId).push(e);
    }
    return map;
  }, [entries]);

  const value = useMemo(() => {
    const entriesFor = (familyId) => byFamily.get(familyId) || [];
    return {
      loading,
      // The read stopped before it answered (#647) — src/domain/reading.js.
      failed: read.failed,
      entries,
      entriesFor,
      balanceFor: (familyId) => coreSync().balanceOwing(entriesFor(familyId)),
      prepaidFor: (familyId) => coreSync().prepaidBalance(entriesFor(familyId)),
      // `runningBalances` answers `[{ id, runningBalanceMinor }]` rather than
      // the entries, for the reason `unreadableEntryIds` below shares: the
      // caller already holds the documents. Rejoined here so the shape
      // components receive is unchanged.
      ledgerFor: (familyId) => {
        const list = entriesFor(familyId);
        const byId = new Map(list.map((e) => [e.id, e]));
        return coreSync()
          .runningBalances(list)
          .map((r) => ({ ...byId.get(r.id), runningBalanceMinor: r.runningBalanceMinor }));
      },
      // Entries the fold could not interpret. Firestore's offline cache can
      // hold a document the server rejected — a write is applied locally and
      // optimistically before the rejection comes back — so this is reachable
      // in normal operation, not just after a bad migration. Non-empty means a
      // displayed total is incomplete and should say so.
      unreadable: () => {
        const byId = new Map(entries.map((e) => [e.id, e]));
        return coreSync().unreadableEntryIds(entries).map((id) => byId.get(id));
      },
      // The "You're owed $X" header and Home's tile: across the families
      // named — the ones the studio has (#640). A ledger group whose family
      // is not among them is not counted, so the header is the rows' sum.
      totalOwed: (familyIds) => owedByFamilies(familyIds || [], byFamily, (list) => coreSync().balanceOwing(list)),
      // A hand-entered transaction: `input` is `transactionInput`'s, and the
      // document is `transactionDocument` (src/domain/familyAccount.js).
      // `followOn` (#592): an uncommitted batch to queue once this entry is
      // acknowledged and never if it is refused — Add Transaction's payment
      // receipt. See `releaseFollowOn` in writes.js.
      appendEntry: async (input, { followOn = null } = {}) => {
        if (!user) return null;
        // A hand-entered transaction is a genuine append: two identical cash
        // payments on the same day are two payments, so a random ID is correct
        // here. Generated entries use derived IDs instead (see useAttendance).
        const entry = transactionDocument(
          { ...input, id: input.id || freeId(input.kind) }, serverTimestamp(),
        );
        const batch = writeBatch(db);
        batch.set(docRef(user.uid, COLLECTIONS.ledgerEntries, entry.id), entry);
        // writeBatch works offline — it is a blind write with no reads, unlike
        // runTransaction, which needs a server round trip and fails outright
        // when disconnected. Every multi-document write in this layer is a
        // batch.
        await batch.commit({ followOn });
        return entry;
      },
      // Entries are tombstoned rather than deleted so a device that was offline
      // when the reversal happened converges instead of resurrecting the entry
      // from its own cache.
      //
      // Both halves of the tombstone. `voidedAt` alone would not survive its own
      // trip through the local cache: a serverTimestamp() has no value until the
      // server acknowledges the write, and the SDK's default
      // ServerTimestampBehavior of 'none' reads it back as null — so on the
      // device that just voided the entry, every fold would call it live until
      // the network came back. Issue #70.
      voidEntry: async (entryId) => {
        if (!user) return;
        const batch = writeBatch(db);
        batch.set(
          docRef(user.uid, COLLECTIONS.ledgerEntries, entryId),
          voidEntryDocument(serverTimestamp()),
          { merge: true }
        );
        await batch.commit();
      },
    };
  }, [byFamily, entries, loading, read.failed, user]);

  return <LedgerContext.Provider value={value}>{children}</LedgerContext.Provider>;
}

export function useLedger() {
  const ctx = useContext(LedgerContext);
  if (!ctx) throw new Error('useLedger must be used within a LedgerProvider');
  return ctx;
}
